import '#lit/components/base/list/index.js';
import '#lit/components/base/loader/index.js';
import '#lit/components/base/list_item/index.js';
import '#lit/components/base/dropdown/index.js';

import type {CustomKeyupEvent} from '../input/index.js';
import type FlyInput from '../input/index.js';
import {Variant as FlyInputVariant} from '../input/index.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {property, query, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import multiselectStyles from '../multiselect_async/style.scss';
import type {RequestExecutionHandler} from '#lit/utils/http.js';
import {RequestHandler} from '#lit/utils/http.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {debounce} from '#utils/debounce.js';
import type {Nullable} from '#lit/types/general.js';
import styles from './style.scss';
import type FlyDropdown from '#lit/components/base/dropdown/index.js';
import {nextTick} from '#lit/utils/index.js';
import {repeat} from 'lit/directives/repeat.js';
import {v4} from 'uuid';

export const STARTING_PAGE_COUNT = 1;
export const EMPTY_QUERY = '';
export const MIN_LENGTH = 1;
export const SEARCH_DEBOUNCE_WAIT_MS = 200;

export enum AsyncSelectOptionTags {}

export enum AsyncSelectEvents {
  SELECTED = 'selected',
  DESELECTED = 'deselected',
}

export type SelectionEventDetail = {
  selected?: Option;
};

export type AsyncSelectSelectionEvent = CustomEvent<SelectionEventDetail>;

export interface Option<T = unknown> {
  value: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  tags?: Set<string>;
  record: T;
}

export class AsyncSelectQueryState {
  requestCount = 0;
  page = STARTING_PAGE_COUNT;
  more = true;
  requestHandler: RequestHandler;
  results: Set<Option['value']> = new Set();
  error?: Error;

  static FETCH_KEY = 'select-async-fetch';

  constructor(
    private host: SelectAsync,
    public query: string,
    private executionFn: RequestExecutionHandler
  ) {
    this.query = query;
    this.executionFn = executionFn;

    this.requestHandler = RequestHandler.create({
      [this.getCacheKey()]: this.executionFn,
    });
  }

  getCacheKey(): string {
    return `${AsyncSelectQueryState.FETCH_KEY}-${this.query}-${this.page}`;
  }

  get hasError(): boolean {
    return !!this.error;
  }

  abortCurrentRequest(): void {
    this.requestHandler.abortRequest(this.getCacheKey());
  }

  execute({paginating}: {paginating: boolean} = {paginating: false}): void {
    this.host.fetching = true;

    if (paginating) {
      this.host.paginating = true;
      this.page++;
    }

    this.host.requestUpdate();

    this.requestHandler.executeAllRequests();
  }
}

/**
 * A single-select, async search component.
 */
export default abstract class SelectAsync extends FlywheelComponent {
  /**
   * Async select propties.
   */
  // The actual serach query.
  @property()
  query = EMPTY_QUERY;
  // The placeholder for the select box.
  @property()
  placeholder?: string;
  // For managing the dropdown.
  @property({type: Boolean})
  open = false;
  // Shows a searchbar
  @property({type: Boolean})
  searchbar = false;
  @property({type: Boolean})
  disabled = false;
  @property()
  initial: Nullable<Option['value']> = null;
  @property({type: Boolean})
  autofocusSearch = false;

  /**
   * In-memory objects; used for rendering options, etc.
   */
  @state()
  queryStates: Map<string, AsyncSelectQueryState> = new Map();
  // A map of object value to option result.
  @state()
  options: Map<string, Option> = new Map();
  // A list of options values;
  @state()
  protected _selected: Nullable<Option['value']> = null;

  private _optionsObserver: IntersectionObserver | null = null;

  /**
   * Boolean state flags for loading indicators, etc.
   */
  // Fetch state; defaults to true to trigger loading indicator.
  @state()
  fetching = true;
  @state()
  typing = false;
  @state()
  searching = false;
  @state()
  paginating = false;

  /**
   * DOM Elements
   */
  @query('#option-list')
  $optionList?: HTMLElement;
  @query('#searchbar')
  $searchbar?: FlyInput;
  @query('fly-dropdown')
  $dropdown?: FlyDropdown;
  @query('slot[name="action"]')
  $actionSlot?: HTMLSlotElement;

  /**
   * Configuration
   */
  maxLabelLength?: number;
  key = v4();

  /**
   * Debounced methods
   */
  private debouncedSearchFn = debounce(
    this.search.bind(this),
    SEARCH_DEBOUNCE_WAIT_MS
  );

  static get styles() {
    return [multiselectStyles, styles];
  }

  beforeLoad() {
    this.setInitialOption();
  }

  setInitialOption() {
    if (this.initial) {
      this._selected = this.initial;
      this.options.forEach(option => (option.selected = false));
      const option = this.options.get(this.initial) || {
        value: this.initial,
        label: this.initial,
        selected: true,
        record: this.initial,
      };
      option.selected = true;
      this.options.set(this.initial, option);
    }
  }

  /**
   * Gets the state for the current query.
   */
  get currentState(): AsyncSelectQueryState | undefined {
    return this.queryStates.get(this.query);
  }

  /**
   * Returns the currently selected value.
   */
  get selectedValue(): Nullable<Option['value']> {
    return this._selected;
  }

  get selected(): Option | undefined {
    return this.options.get(this._selected as string);
  }

  /**
   * Determines wehether options can be rendered.
   */
  get canRenderOptions(): boolean {
    return (
      this.paginating || (!this.fetching && !this.paginating && !this.searching)
    );
  }

  /**
   * =====================================
   *  Abstract handlers to be implemented
   * =====================================
   */
  // Used for registering query states, etc.
  abstract fetchHandler(
    signal: AbortSignal,
    ...args: unknown[]
  ): Promise<unknown>;

  /**
   * Called whenever an option is clicked.
   */
  abstract onOptionClick(option: Option): void;

  /**
   * ======================
   *  Lifecycle hook logic
   * ======================
   */
  // Calls event methods when certain properties change.
  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('query') && this.query) {
      this.onQueryUpdate();
    }

    if (
      changedProperties.has('open') &&
      this.open &&
      !this.hasQueryState(this.query)
    ) {
      this.onQueryUpdate();
    }

    if (changedProperties.has('initial')) {
      this.setInitialOption();
    }
  }

  rendered() {
    // Adds scroll listener to the options list.
    // Used to paginate records.
    this.attachOptionScrollListener();
  }

  /**
   * =================
   *  Private methods
   * =================
   */
  /**
   * Registers a query state, given a query string.
   */
  protected registerQueryState(query: string): void {
    const state = new AsyncSelectQueryState(
      this,
      query,
      this.fetchHandler.bind(this)
    );

    state.requestHandler.addHook(async () => {
      state.requestCount++;
      this.fetching = false;
      this.paginating = false;
      this.searching = false;
      this.requestUpdate();
      await this.performUpdate();

      if (this.$dropdown?.open) {
        nextTick(async () => {
          this.$dropdown?.surfacePosition();
          this.$dropdown?.requestUpdate();
          await this.performUpdate();
        });
      }
    });

    this.queryStates.set(query, state);
  }

  private resetStateFlags(): void {
    this.fetching = false;
    this.paginating = false;
    this.typing = false;
    this.searching = false;
  }

  private hasOptionsAction(): boolean {
    return !!this.$actionSlot?.assignedNodes().length;
  }

  /**
   * ====================
   *  Query mgmt methods
   * ====================
   */
  /**
   * Determines whether a query state exists / has been used for
   * a given qeury.
   */
  hasQueryState(query: string): boolean {
    return (
      !!this.queryStates.has(query) &&
      !!this.queryStates.get(query)?.requestCount
    );
  }

  resetQueryStates() {
    this.queryStates.clear();
  }

  /**
   * First every time the query property changes.
   */
  onQueryUpdate(): void {
    if (!this.hasQueryState(this.query)) {
      this.registerQueryState(this.query);
      this.fetch();
    }
  }

  clearSearch() {
    if (!this.$searchbar) return;
    this.$searchbar.value = EMPTY_QUERY;
  }

  /**
   * ===============
   *  Fetch methods
   * ===============
   */
  // Actual executes fetch
  async fetch(): Promise<void> {
    this.fetching = true;
    this.requestUpdate();
    await this.performUpdate();
    // Fires requests for fetching, etc.
    this.queryStates.get(this.query)?.execute();
  }

  // Actual executes fetch
  async paginate(): Promise<void> {
    this.paginating = true;
    this.fetching = true;

    this.requestUpdate();
    await this.performUpdate();

    this.queryStates.get(this.query)?.execute({paginating: true});
  }

  async search(query: string): Promise<void> {
    // First we check to make sure the user is actually typing.
    // If they're not, we don't want to search.
    if (!this.typing) return;

    if (this.hasQueryState(query)) {
      this.query = query;
      this.requestUpdate();
      return;
    }

    // Set the appropriate boolean flags.
    this.paginating = false;
    this.fetching = true;
    this.searching = !!query.length;
    this.typing = false;

    this.requestUpdate();
    await this.performUpdate();

    // Update the query, which triggers a fetch.
    this.query = query;
  }

  /**
   * =============
   *  Event hooks
   * =============
   */
  attachOptionScrollListener(): void {
    if (!this.$optionList) return;

    this._optionsObserver = new IntersectionObserver(
      entries => {
        this.onOptionListScroll(this.$optionList!, entries);
      },
      {root: this.$optionList, threshold: 1}
    );

    if (this.$optionList.lastElementChild) {
      this._optionsObserver.observe(this.$optionList.lastElementChild);
    }
  }

  onOptionListScroll(
    optionList: HTMLElement,
    entries: IntersectionObserverEntry[]
  ): void {
    const state = this.queryStates.get(this.query);
    if (!state?.more) return;

    const hasScrollbar = optionList.clientHeight < optionList.scrollHeight;
    const isAtBottom = entries[0].isIntersecting === true;
    const shouldPaginate = !hasScrollbar || isAtBottom;

    if (!this.fetching && shouldPaginate && state.more) {
      this.paginate();
    }
  }

  onDropdownOpened() {
    this.open = true;
  }

  onDropdownClosed() {
    this.open = false;
  }

  onInput(e: CustomEvent<{value: string}>) {
    this.debouncedSearchFn(e.detail.value);
  }

  onKeyup(_: CustomKeyupEvent) {
    this.typing = true;
  }

  selectOption(option: Option) {
    this._selected = option.value;
    this.open = false;
    this.requestUpdate();
    this.dispatchSelectionEvent(AsyncSelectEvents.SELECTED);
  }

  deselectOption(_: Option) {
    this._selected = null;
    this.requestUpdate();
    this.dispatchSelectionEvent(AsyncSelectEvents.DESELECTED);
  }

  disconnectedCallback(): void {
    if (this._optionsObserver) {
      this._optionsObserver.disconnect();
      this._optionsObserver = null;
    }
  }

  /**
   * ==================
   *  Dispatch methods
   * ==================
   */
  dispatchSelectionEvent(
    type: AsyncSelectEvents.SELECTED | AsyncSelectEvents.DESELECTED
  ): void {
    const detail: SelectionEventDetail = {
      selected: this.selected,
    };
    const event = new CustomEvent<SelectionEventDetail>(type, {detail});
    this.dispatchEvent(event);
  }

  /**
   * ============================
   *  Methods for rendering HTML
   * ============================
   */
  renderSearchbar(): TemplateResult {
    return html`
      <fly-input
        id="searchbar"
        hasicon
        type="search"
        @input=${this.onInput.bind(this)}
        @keyup=${this.onKeyup.bind(this)}
        minlength=${MIN_LENGTH}
        variant=${FlyInputVariant.BORDERLESS}
        ?autofocus=${this.autofocusSearch}
        placeholder="Search"
      >
        <div slot="icon">
          <mwc-icon icon="search">search</mwc-icon>
        </div>
      </fly-input>
    `;
  }

  renderOption(option: Option): TemplateResult {
    return html`
      <fly-list-item
        ?selected=${this.selectedValue === option.value}
        @click=${() => {
          this.onOptionClick(option);
        }}
        @deselected=${() => {
          if (option.value === this.selectedValue) {
            this.deselectOption(option);
          }
        }}
        @selected=${() => {
          this.selectOption(option);
        }}
      >
        ${option.label}
      </fly-list-item>
    `;
  }

  renderStateOptions(): TemplateResult {
    const queryState = this.queryStates.get(this.query);
    if (!queryState) return html``;

    const options = Array.from(queryState.results)
      .filter(r => this.options.has(r))
      .map(r => this.options.get(r)) as Option[];

    return html`
      <fly-list>
        ${repeat(
          options,
          option => `${this.key}-${option.value}`,
          option => this.renderOption(option)
        )}
      </fly-list>
    `;
  }

  renderSelectBox(): TemplateResult {
    const option = this.selectedValue
      ? this.options.get(this.selectedValue)
      : null;

    const label = !this.selectedValue
      ? (this.placeholder ?? 'Select')
      : (option?.label ?? this.selectedValue);

    return html`
      <div
        @click=${() => (this.open = !this.open)}
        tabindex="1"
        slot="anchor"
        class="fly-multiselect-async__anchor"
        title="${label}"
      >
        ${!this.selectedValue
          ? html`
              <span class="fly-multiselect-async__anchor__label">
                ${label}
              </span>
            `
          : html`
              <div class="fly-multiselect-async__anchor__value">
                ${this.maxLabelLength
                  ? label.substring(0, this.maxLabelLength) + '...'
                  : label}
              </div>
            `}
        <div
          class="fly-multiselect__anchor__icon"
          class=${classMap({
            'fly-multiselect__anchor__icon--open': this.open,
          })}
        >
          <fly-icon
            style=${styleMap({
              '--fly-icon-size': '16px',
            })}
            icon=${this.open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          ></fly-icon>
        </div>
      </div>
    `;
  }

  renderSurface(): TemplateResult {
    return html`
      <div class="fly-multiselect-async__surface" slot="surface">
        <div class="fly-multiselect-async__surface__grid">
          <div
            class=${classMap({
              'fly-multiselect-async__surface__grid__top': true,
              'fly-multiselect-async__surface__grid__top--has-action':
                this.hasOptionsAction(),
            })}
          >
            ${this.searchbar ? this.renderSearchbar() : ''}
            <slot name="action"></slot>
          </div>
          <div
            id="option-list"
            class="fly-multiselect-async__surface__grid__bottom"
          >
            <fly-loader
              ?loading=${true}
              style=${styleMap({
                '--fly-loader-size': '24px',
                '--fly-loader-margin': '24px auto',
                display: !this.fetching || this.paginating ? 'none' : 'inherit',
              })}
            ></fly-loader>
            ${this.canRenderOptions ? this.renderStateOptions() : ''}
            <!-- Pagination loader -->
            <div
              style=${styleMap({
                height: '12px',
                margin: '12px',
                visibility:
                  this.paginating && !this.searching ? 'visible' : 'hidden',
                overflow:
                  this.paginating && !this.searching ? 'visible' : 'hidden',
                display: this.fetching && !this.paginating ? 'none' : 'initial',
              })}
            >
              <fly-loader
                ?loading=${true}
                style="--fly-loader-size: 12px; --fly-loader-margin: 0 auto;"
              ></fly-loader>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="fly-multiselect-async">
        <fly-dropdown
          ?active=${this.open}
          ?disabled=${this.disabled}
          @opened=${this.onDropdownOpened.bind(this)}
          @closed=${this.onDropdownClosed.bind(this)}
        >
          ${this.renderSelectBox()} ${this.renderSurface()}
        </fly-dropdown>
      </div>
    `;
  }
}
